import React, { PureComponent } from 'react';
import dashboardComponents from './dashboards';
import { ErrorMessage } from './base';
import { CenteringDiv } from './base/styled';

// Helper function-
// Tell the server that the dashboard is all loaded and spinners are gone.
// This is not called if the browser is an Electron renderer process,
// or if metadata.electronCapture is set to false.
async function notifyCompleted(path) {
  const opts = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    mode: 'same-origin',
    credentials: 'include',
    body: JSON.stringify({ uri: path }),
  };
  try {
    const response = await fetch('/api/completed', opts);
    if (response) {
      console.log('/api/completed: response.status', response.status);
    } else {
      console.log('No response from /api/completed...');
    }
  } catch (e) {
    console.error(e);
    console.log('rethrowing...');
    throw e;
  }
}

const wiredComponents = new Map();

/*
 * dataSubscriber.jsx exports a function that takes an object "entry" from dashboards.json
 * sent over the wire by the server, and returns a component that displays the dashboard and
 * handles the entire client side of the Pub/Sub mechanism linking job workers to React components.
 *
 * This function first looks up the value of entry.component in client/src/components/dashboards/index.js
 * (e.g. "Summary") to get the appropriate React component to use.
 * It then instantiates a wrapper component that embeds an instance of the dashboard component,
 * and communicates with the server in its lifecycle methods via AJAX and websockets to keep
 * the embedded component always up to date with the latest property values from the server,
 * and also to keep the server up to date as necessary with the situation on the client.
 *
 * Property values are always in the form of { error, data } pseudo-tuple objects.
 *
 * Note that the server does not send the actual values of the fields in entry.remoteProps
 * (with the job names, intervals, and config values).
 * Instead entry.remoteProps arrives here as an array of property names.
 * The client needs the names of the fields, since they correspond to property names.
 * But it doesn't need to know the values.
 */
const dataSubscriber = function (entry, scrollerPaneIndex = Number.NaN) {

  const {
    path,
    metadata: { electronCapture = true },
    component: componentName,
    localProps = {},
    remotePropNames = [],
  } = entry;

  const dbc = dashboardComponents;
  const WrappedComponent = dbc[componentName];

  // Need wiredComponents to eliminate endless mounting and unmounting
  const wiredComponentsKey = (Number.isNaN(scrollerPaneIndex) || scrollerPaneIndex === 0)
    ? path
    : `${path}-pane-${scrollerPaneIndex}`;
  let wiredComponent = wiredComponents.get(wiredComponentsKey);
  if (!wiredComponent) {
    class DashboardWrapper extends PureComponent {

      constructor() {
        super();
        this.state = { caughtError: null };
        this.mounted = false;
        this.subscriberId = null;
        this.rooms = [];
        this.propertiesByRoom = {};
        this.socketTrouble = false;
        this.dataListener = this.dataListener.bind(this);
        this.errorListener = this.errorListener.bind(this);
        this.disconnectListener = this.disconnectListener.bind(this);
        this.connectListener = this.connectListener.bind(this);
      }

      async componentDidMount() {
        this.mounted = true;
        this.requestSubscription();
        // document.addEventListener('visibilitychange', this.visibilityChangeListener);
        window.dataSocket.on('disconnect', this.disconnectListener);
        window.dataSocket.on('connect', this.connectListener);
      }

      async componentWillUnmount() {
        this.mounted = false;
        window.dataSocket.removeListener('disconnect');
        window.dataSocket.removeListener('connect');
        this.cancelSubscription();
      }

      componentDidCatch(error, _info) {
        const message = error.toString();
        console.log('App.componentDidCatch: ' + message);
        if (this.mounted) {
          // document.removeEventListener('visibilitychange', this.visibilityChangeListener);
          window.dataSocket.removeListener('disconnect', this.disconnectListener);
          window.dataSocket.removeListener('connect', this.connectListener);
          this.cancelSubscription();
          this.setState({ caughtError: message });
          setTimeout(() => {
            if (this.mounted) {
              window.location.reload(true);
            }
          }, 60000);
        }
      }

      async requestSubscription() {
        const opts = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          mode: 'same-origin',
          credentials: 'include',
          body: JSON.stringify({ path, subscriberId: this.subscriberId }),
        };
        try {
          const response = await fetch('/jobs/connect', opts);
          if (response) {
            const json = await response.json();
            this.subscriberId = json.subscriberId;
            const roomsByStateField = json.rooms;
            Object.keys(roomsByStateField).forEach((stateField) => {
              const room = roomsByStateField[stateField];
              this.rooms.push(room);
              this.propertiesByRoom[room] = stateField;
            });
            window.dataSocket.on('roomData', this.dataListener);
            window.dataSocket.on('roomError', this.errorListener);
            window.dataSocket.emit('joinRooms', {
              subscriberId: this.subscriberId,
              path,
              rooms: this.rooms,
            });
          } else {
            console.log('No response from /jobs/connect...');
            console.log(JSON.stringify(opts));
          }
        } catch (e) {
          console.error(e);
          console.log('rethrowing...');
          throw e;
        }
      }

      cancelSubscription() {
        window.dataSocket.removeListener('roomData', this.dataListener);
        window.dataSocket.removeListener('roomError', this.errorListener);
        try {
          window.dataSocket.emit('leaveRooms', {
            subscriberId: this.subscriberId,
            rooms: this.rooms,
          });
        } catch (e) {
          console.log('dataSubscriber.jsx: cancelSubscription threw error: ' + e);
        }
        this.propertiesByRoom = {};
        this.rooms = [];
      }

      isStateComplete() {
        let complete = true;
        const state = this.state;
        remotePropNames.forEach((field) => {
          if (!((state[field] && (state[field].data || state[field].error)))) {
            complete = false;
          }
        });
        return complete;
      }

      dataListener(roomData) {
        if (this.mounted) {
          const { room, data } = roomData;
          const field = this.propertiesByRoom[room];
          const update = {};
          update[field] = { data };
          this.setState(update);
          window.dataSocket.emit('acknowledge', { subscriberId: this.subscriberId });
          if (Number.isNaN(scrollerPaneIndex) || scrollerPaneIndex === 0) {
            if (this.isStateComplete()) {
              if (typeof window.process === 'object' && window.process.type === 'renderer') {
                this.cancelSubscription(); // Electron renderer process
              } else {
                if (electronCapture) {
                  notifyCompleted(path); // Ordinary browser
                }
              }
            }
          }
        }
      }

      errorListener(roomError) {
        if (this.mounted) {
          const { room, error } = roomError;
          const field = this.propertiesByRoom[room];
          const update = {};
          update[field] = { error };
          this.setState(update);
          window.dataSocket.emit('acknowledge', { subscriberId: this.subscriberId });
        }
      }

      disconnectListener() {
        this.socketTrouble = true;
      }

      connectListener() {
        if (this.socketTrouble) {
          this.socketTrouble = false;
          // Wait two seconds
          setTimeout(() => {
            // index.js didn't call window.reload after getting a serverinfo event indicating this was a server restart.
            this.cancelSubscription();
            this.requestSubscription();
          }, 2000);
        }
      }

      render() {
        if (this.state.caughtError) {
          return (
            <div className="dashboard">
              <CenteringDiv>
                <ErrorMessage error={this.state.caughtError} />
              </CenteringDiv>
            </div>
          );
        }
        const actualProps = Object.assign({}, localProps, this.state);
        return <WrappedComponent {...actualProps} />;
      }
    }
    wiredComponent = DashboardWrapper;
    wiredComponents.set(wiredComponentsKey, wiredComponent);
  }

  return wiredComponent;
};

export default dataSubscriber;
